/**
 * Core Service page translations - English
 */

export default {
  hero: {
    title: 'Core Services',
    subtitle:
      'An end-to-end service portfolio covering strategy, infrastructure delivery, and daily operations across the full digital transformation lifecycle',
    imageAlt: 'Core services background image'
  },

  nav: {
    title: 'Service Categories'
  },

  portfolio: {
    eyebrow: 'Service Portfolio',
    introTitle: 'End-to-End Service Portfolio',
    introDescription:
      'Our service portfolio is built around the full lifecycle of enterprise digital transformation, from strategic planning and implementation to continuous operations, helping clients achieve consistent, stable, and repeatable IT service capabilities across regions.',
    serviceContentTitle: 'Service Content',
    coreValueTitle: 'Core Value',
    sections: [
      {
        id: 'digital-workplace',
        icon: 'fas fa-laptop',
        image: '/images/coreService/it-base.jpg',
        title: 'Digital Workplace',
        subtitle: 'Efficient, stable, and unified digital office experience',
        description:
          'We support daily employee work and collaboration scenarios with endpoint, account, collaboration tool, remote work, and IT asset lifecycle services to improve productivity and user experience.',
        serviceItems: [
          { icon: 'fas fa-desktop', text: 'Endpoint device and office environment support for laptops, desktops, and mobile devices' },
          { icon: 'fas fa-user-cog', text: 'Employee onboarding and offboarding IT support for accounts, devices, and permissions' },
          { icon: 'fas fa-comments', text: 'Office software and collaboration tool support' },
          { icon: 'fas fa-mobile-alt', text: 'Remote work and mobile work support' },
          { icon: 'fas fa-boxes', text: 'IT asset and device lifecycle management' },
          { icon: 'fas fa-chart-line', text: 'Digital workplace experience optimization' }
        ],
        valueItems: [
          {
            icon: 'fas fa-globe-asia',
            title: 'Regional Governance',
            points: ['Unified SLA standards', 'Centralized service management', 'Cross-region coordination']
          },
          {
            icon: 'fas fa-building',
            title: 'Local Delivery',
            points: ['Local engineer network in each country', 'Rapid response mechanism', 'Combined onsite and remote delivery']
          },
          {
            icon: 'fas fa-language',
            title: 'Multilingual Support',
            points: ['Chinese / English / Japanese / Korean', 'Cross-cultural communication', 'Consistent multi-region experience']
          }
        ]
      },
      {
        id: 'data-center-network',
        icon: 'fas fa-network-wired',
        image: '/images/coreService/base-operatoin.jpg',
        title: 'Data Center & Network Infrastructure',
        subtitle: 'Keep core business systems stable and available',
        description:
          'We cover enterprise networks, office networks, store networks, data center networks, servers, and storage devices, helping clients improve infrastructure reliability and business continuity.',
        serviceItems: [
          { icon: 'fas fa-network-wired', text: 'Enterprise network deployment and maintenance for LAN / WAN / WiFi' },
          { icon: 'fas fa-building', text: 'Store network and office network support' },
          { icon: 'fas fa-tools', text: 'Network device installation and configuration, including switches and firewalls' },
          { icon: 'fas fa-search', text: 'Network troubleshooting and performance optimization' },
          { icon: 'fas fa-server', text: 'Data center network and server room network management' },
          { icon: 'fas fa-database', text: 'Server and storage device support' },
          { icon: 'fas fa-clipboard-check', text: 'Data center operations and inspection' }
        ],
        valueItems: [
          {
            icon: 'fas fa-shield-alt',
            title: 'Higher Availability',
            points: ['Improve system stability and availability', 'Reduce core system failure probability', 'Build sustainable operations routines']
          },
          {
            icon: 'fas fa-exclamation-triangle',
            title: 'Lower Interruption Risk',
            points: ['Locate network and device issues quickly', 'Reduce business downtime', 'Support emergency onsite response']
          },
          {
            icon: 'fas fa-business-time',
            title: 'Business Continuity',
            points: ['Support continuous critical operations', 'Cover headquarters, stores, and data centers', 'Fit multi-region operations management']
          }
        ]
      },
      {
        id: 'retail-store-ops',
        icon: 'fas fa-store',
        image: '/images/industrySolution/industry-ticketAI.jpg',
        title: 'Retail Store IT Operations',
        subtitle: 'Standardized support for multi-country, multi-store operations',
        description:
          'For retail chains and cross-region businesses, we provide scalable and standardized store IT operations services to keep every store running reliably with a consistent support experience.',
        serviceItems: [
          { icon: 'fas fa-headset', text: 'Daily store IT operations and incident response' },
          { icon: 'fas fa-desktop', text: 'Store device management, including POS and endpoint devices' },
          { icon: 'fas fa-network-wired', text: 'Store network and system support' },
          { icon: 'fas fa-plus-circle', text: 'New store IT deployment and go-live support' },
          { icon: 'fas fa-sync-alt', text: 'Store relocation, upgrade, and closure support' }
        ],
        valueItems: [
          {
            icon: 'fas fa-check-circle',
            title: 'Stable Stores',
            points: ['Keep every store running steadily', 'Reduce device and network impact', 'Cover key daily operations scenarios']
          },
          {
            icon: 'fas fa-bolt',
            title: 'Fast Response',
            points: ['Respond to issues quickly', 'Reduce impact on business hours', 'Combine remote diagnosis with onsite handling']
          },
          {
            icon: 'fas fa-sitemap',
            title: 'Unified Management',
            points: ['Manage stores across regions consistently', 'Standardize delivery processes', 'Track service quality centrally']
          }
        ]
      },
      {
        id: 'cloud-operations',
        icon: 'fas fa-cloud',
        image: '/images/coreService/cloud-it.jpg',
        title: 'Cloud & Cloud Operations',
        subtitle: 'Flexible, efficient, and continuously optimized cloud architecture',
        description:
          'We help enterprises plan, deploy, migrate, and operate cloud environments, covering public cloud, hybrid cloud, cloud resource management, monitoring, security, and cost optimization.',
        serviceItems: [
          { icon: 'fas fa-project-diagram', text: 'Cloud environment deployment and architecture design for public and hybrid cloud' },
          { icon: 'fas fa-cloud-upload-alt', text: 'Cloud migration and system modernization to the cloud' },
          { icon: 'fas fa-cogs', text: 'Cloud resource management and operations' },
          { icon: 'fas fa-shield-alt', text: 'Cloud monitoring and security management' },
          { icon: 'fas fa-chart-pie', text: 'Cloud cost optimization' }
        ],
        valueItems: [
          {
            icon: 'fas fa-expand-arrows-alt',
            title: 'Flexible Scale',
            points: ['Improve IT flexibility and scalability', 'Support traffic peaks and rapid deployment', 'Fit multi-cloud and hybrid scenarios']
          },
          {
            icon: 'fas fa-coins',
            title: 'Cost Efficiency',
            points: ['Optimize resource usage', 'Control cloud cost structure', 'Continuously track resource efficiency']
          },
          {
            icon: 'fas fa-rocket',
            title: 'Fast Business Change',
            points: ['Support fast-changing business needs', 'Shorten application launch cycles', 'Improve operational transparency in the cloud']
          }
        ]
      },
      {
        id: 'digital-transformation',
        icon: 'fas fa-rocket',
        image: '/images/home/home-ai-empowerment.jpg',
        title: 'Digital Transformation',
        subtitle: 'Move from traditional IT operations to data-driven management',
        description:
          'We help enterprises turn IT capabilities into repeatable and scalable digital operating systems across membership operations, marketing automation, lightweight service portals, knowledge management, and office automation.',
        serviceItems: [
          { icon: 'fas fa-users', text: 'CRM software development for membership, user tags, points, engagement, and repeat-purchase operations' },
          { icon: 'fas fa-bullhorn', text: 'Marketing automation connecting websites, CRM, email, and messaging channels into closed-loop journeys' },
          { icon: 'fas fa-mobile-alt', text: 'Mini program development for booking, repair, query, membership, and service scenarios' },
          { icon: 'fas fa-brain', text: 'AI knowledge base for FAQ, SOP, ticket experience, product materials, semantic search, and access control' },
          { icon: 'fas fa-robot', text: 'Office agents connecting email, approvals, meeting notes, task flows, and knowledge Q&A' }
        ],
        valueItems: [
          {
            icon: 'fas fa-database',
            title: 'Data Driven',
            points: ['Shift from people-driven to data-driven operations', 'Accumulate operational data assets', 'Improve management decisions']
          },
          {
            icon: 'fas fa-tachometer-alt',
            title: 'Efficient & Transparent',
            points: ['Improve operational efficiency and transparency', 'Reduce repetitive manual work', 'Unify processes and service entry points']
          },
          {
            icon: 'fas fa-layer-group',
            title: 'Repeatable Scale',
            points: ['Build a repeatable IT management system', 'Support multi-region rollout', 'Create a continuous optimization mechanism']
          }
        ]
      }
    ]
  }
}
